import { useState, useCallback, useContext } from "react";
import useTranslation from "../../../../../packages/locales/translations/util/useTranslation";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { MediaTasksModal } from "./MediaTasksModal/MediaTasksModal";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { TaskCardRenderer } from "./TaskCardRenderer";
import { MediaTaskListItem } from "./types";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import styles from "./media-tasks-section.module.css";

interface MediaTasksSectionViewProps {
  allTasks: MediaTaskListItem[];
  totalTaskCount: number;
  isLoading: boolean;
  itemsMap: Map<string, Item>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  testId?: string;
}

/**
 * Pure presentation component for MediaTasksSection
 * Receives data as props, handles only UI rendering and user interactions
 * Can be reused by both single-media and all-medias variants
 */
export const MediaTasksSectionView = ({
  allTasks,
  totalTaskCount,
  isLoading,
  itemsMap,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  testId = "media-tasks-list-item",
}: MediaTasksSectionViewProps) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [, setSearchParams] = useSearchParamState();
  const { toggleModal } = useContext(ModalContext);

  const handleTaskClick = useCallback(
    (taskListItem: MediaTaskListItem) => {
      if (taskListItem.type === "v1") {
        toggleModal({
          type: ModalType.TaskDetail,
          payload: {
            taskId: taskListItem.task.id,
            taskType: taskListItem.task.task_type,
          },
        });
      } else {
        setSearchParams((params) => {
          params.set("taskViewer", String(taskListItem.task.id));
        });
      }
    },
    [toggleModal, setSearchParams]
  );

  const handleV1TaskClick = useCallback(
    (taskId: string, taskType: string) => {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId, taskType },
      });
    },
    [toggleModal]
  );

  const count = allTasks.length;

  // Loading state
  if (isLoading && count === 0) {
    return null;
  }

  // Empty state
  if (count === 0) {
    return null;
  }

  // Single task: render inline
  if (count === 1 && allTasks[0]) {
    return (
      <div className={styles["single-task-container"]}>
        <ul className={styles["task-list"]}>
          <TaskCardRenderer
            taskListItem={allTasks[0]}
            itemsMap={itemsMap}
            onV1TaskClick={handleV1TaskClick}
            onV2TaskClick={handleTaskClick}
          />
        </ul>
      </div>
    );
  }

  // Multiple tasks: render clickable row + modal
  return (
    <>
      <FixaListView id="media-tasks-list">
        <FixaListViewItem
          size="medium"
          control="navigational"
          controlIcon="chevronRight"
          inset
          onClick={() => setShowModal(true)}
          id="media-tasks-list-item"
          data-testid={testId}
          title={
            <FixaText
              textStyle="Heading.XS"
              className={styles["tasks-heading"]}
            >
              {t("TasksString")}
            </FixaText>
          }
          description={t("relatedTasksCountString", { count: totalTaskCount })}
        />
      </FixaListView>
      <MediaTasksModal
        tasks={allTasks}
        showModal={showModal}
        onClose={() => setShowModal(false)}
        totalCount={totalTaskCount}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={onLoadMore}
      />
    </>
  );
};
